import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";

const SeoPageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    page_Id: "",
    page_url: "",
    page_name: "",
    meta_title: "",
    meta_keywords: "",
    meta_description: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState("");
  const [managePageData, setManagePageData] = useState([]);
  const [token] = useState(localStorage.getItem("cms_token"));

  // console.log(initialLoading, "initialLoading");

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setInitialLoading(true);
        await loadPageData();
        if (isEditMode) {
          await loadSeoData();
        }
      } catch (error) {
        toast.error("Failed to load data");
        console.error("Initialization error:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    initializeComponent();
  }, [id, isEditMode]);

  // Load page data for dropdown
  const loadPageData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get-page-and-content`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "multipart/form-data",
        },
      });
      setManagePageData(response.data.data?.reverse() || []);
    } catch (error) {
      console.error("Error loading page data:", error);
      toast.error("Failed to load page data");
    }
  }, [token]);

  // Load SEO data for editing
  const loadSeoData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/seo-details`, {
        params: { id },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === 200 && response.data.data?.[0]) {
        const seoData = response.data.data[0];
        setFormData({
          page_Id: seoData.page_id || "",
          page_url: seoData.page_url || "",
          page_name: seoData.page_name || "",
          meta_title: seoData.meta_title || "",
          meta_keywords: seoData.meta_keywords || "",
          meta_description: seoData.meta_description || "",
        });
      }
    } catch (error) {
      console.error("Error loading SEO data:", error);
      toast.error("Failed to load SEO data");
    }
  }, [id, token]);

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.page_name?.trim()) {
      newErrors.page_name = "Please select a page";
    }
    if (!formData.page_url?.trim()) {
      newErrors.page_url = "Please enter page URL";
    }
    if (!formData.meta_title?.trim()) {
      newErrors.meta_title = "Please enter meta title";
    }
    if (!formData.meta_description?.trim()) {
      newErrors.meta_description = "Please enter meta description";
    }
    if (!formData.meta_keywords?.trim()) {
      newErrors.meta_keywords = "Please enter meta keywords";
    }

    return newErrors;
  }, [formData]);

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setPageError("");

    try {
      const apiEndpoint = isEditMode ? "/update-seo" : "/add-seo";
      const payload = {
        page_name: formData.page_name,
        page_url: formData.page_url,
        meta_title: formData.meta_title,
        meta_keywords: formData.meta_keywords,
        meta_description: formData.meta_description,
        page_id: formData.page_Id,
        ...(isEditMode && { id }),
      };

      const response = await axios.post(
        `${API_BASE_URL}${apiEndpoint}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "200" || response.data.status === 200) {
        toast.success(
          `SEO page ${isEditMode ? "updated" : "added"} successfully`
        );
        navigate("/cms/manageseopage");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setPageError(error.response?.data?.result || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [errors]
  );

  // Handle select dropdown
  const handleSelect = useCallback(
    (fieldName, selectedOption) => {
      if (!selectedOption) {
        // Clear form when no option is selected
        setFormData((prev) => ({
          ...prev,
          [fieldName]: "",
          page_Id: "",
          ...(fieldName === "page_name" && !isEditMode && { page_url: "" }),
        }));
        if (errors[fieldName]) {
          setErrors((prev) => ({ ...prev, [fieldName]: "" }));
        }
        return;
      }

      const selectedPage = managePageData.find(
        (page) => page.page_name === selectedOption.value
      );

      if (selectedPage) {
        setFormData((prev) => ({
          ...prev,
          [fieldName]: selectedOption.value,
          page_Id: selectedPage.id,
          // Auto-populate URL when adding (not editing) and page is selected
          ...(fieldName === "page_name" &&
            !isEditMode && {
              page_url: selectedPage.page_url
                ? selectedPage.page_url
                : selectedPage.page_name
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^a-z0-9-]/g, ""),
            }),
        }));
      }

      if (errors[fieldName]) {
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      }
    },
    [managePageData, errors, isEditMode]
  );

  // console.log(managePageData, "managePageData");
  // console.log(formData, "formData");  

  // Prepare select options
  const pageOptions = managePageData.map((item) => ({
    value: item.page_name,
    label: item.page_name,
  }));

  // Select styles with error handling
  const selectStyles = (errorFieldName) => ({
    control: (provided, state) => ({
      ...provided,
      borderColor: errors[errorFieldName]
        ? "#dc3545"
        : state.isFocused
        ? "#80bdff"
        : provided.borderColor,
      boxShadow: errors[errorFieldName]
        ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)"
        : state.isFocused
        ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
        : provided.boxShadow,
      minHeight: 38,
      "&:hover": {
        borderColor: errors[errorFieldName] ? "#dc3545" : "#80bdff",
      },
    }),
  });

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body" style={{ minHeight: "60vh" }}>
                <div className="d-flex justify-content-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (initialLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  {isEditMode ? "Edit" : "Add"} SEO Page
                </h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <a href="javascript: void(0);">CMS</a>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/cms/manageseopage">Manage SEO Page</Link>
                    </li>
                    <li className="breadcrumb-item active">
                      {isEditMode ? "Edit" : "Add"} SEO Page
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-12">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <h4 className="card-title mb-4">
                      {isEditMode ? "Edit" : "Add"} SEO
                    </h4>

                    <form onSubmit={handleSubmit}>
                      <div className="form-group row mb-4">
                        <label
                          htmlFor="page_name"
                          className="col-form-label col-lg-2"
                        >
                          Page Name <span className="text-danger">*</span>
                        </label>
                        <div className="col-lg-10">
                          <Select
                            id="page_name"
                            name="page_name"
                            options={pageOptions}
                            value={
                              pageOptions.find(
                                (option) => option.label === formData.page_name
                              ) || null
                            }
                            styles={selectStyles("page_name")}
                            onChange={(selectedOption) =>
                              handleSelect("page_name", selectedOption)
                            }
                            placeholder="Select a page..."
                            isClearable
                            isDisabled={loading}
                          />
                          {errors.page_name && (
                            <div className="text-danger mt-1">
                              {errors.page_name}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row mb-4">
                        <label
                          htmlFor="page_url"
                          className="col-form-label col-lg-2"
                        >
                          Page URL <span className="text-danger">*</span>
                        </label>
                        <div className="col-lg-10">
                          <input
                            id="page_url"
                            name="page_url"
                            type="text"
                            value={formData.page_url}
                            className={`form-control ${
                              errors.page_url ? "is-invalid" : ""
                            }`}
                            placeholder="Enter page URL (auto-filled when page is selected)..."
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          {errors.page_url && (
                            <div className="text-danger mt-1">
                              {errors.page_url}
                            </div>
                          )}
                          {!isEditMode && (
                            <small className="form-text text-muted">
                              URL will be auto-generated when you select a page
                              name, but you can modify it as needed.
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row mb-4">
                        <label
                          htmlFor="meta_title"
                          className="col-form-label col-lg-2"
                        >
                          Meta Title <span className="text-danger">*</span>
                        </label>
                        <div className="col-lg-10">
                          <textarea
                            id="meta_title"
                            name="meta_title"
                            value={formData.meta_title}
                            className={`form-control ${
                              errors.meta_title ? "is-invalid" : ""
                            }`}
                            placeholder="Enter meta title..."
                            onChange={handleInputChange}
                            disabled={loading}
                            rows={2}
                          />
                          {errors.meta_title && (
                            <div className="text-danger mt-1">
                              {errors.meta_title}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row mb-4">
                        <label
                          htmlFor="meta_keywords"
                          className="col-form-label col-lg-2"
                        >
                          Meta Keywords <span className="text-danger">*</span>
                        </label>
                        <div className="col-lg-10">
                          <textarea
                            id="meta_keywords"
                            name="meta_keywords"
                            value={formData.meta_keywords}
                            className={`form-control ${
                              errors.meta_keywords ? "is-invalid" : ""
                            }`}
                            placeholder="Enter meta keywords..."
                            onChange={handleInputChange}
                            disabled={loading}
                            rows={2}
                          />
                          {errors.meta_keywords && (
                            <div className="text-danger mt-1">
                              {errors.meta_keywords}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row mb-4">
                        <label
                          htmlFor="meta_description"
                          className="col-form-label col-lg-2"
                        >
                          Meta Description{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="col-lg-10">
                          <textarea
                            id="meta_description"
                            name="meta_description"
                            value={formData.meta_description}
                            className={`form-control ${
                              errors.meta_description ? "is-invalid" : ""
                            }`}
                            placeholder="Enter meta description..."
                            onChange={handleInputChange}
                            disabled={loading}
                            rows={3}
                          />
                          {errors.meta_description && (
                            <div className="text-danger mt-1">
                              {errors.meta_description}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row justify-content-end">
                        <div className="col-lg-10">
                          {pageError && (
                            <div className="alert alert-danger" role="alert">
                              {pageError}
                            </div>
                          )}

                          <div className="d-flex gap-2">
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  {isEditMode ? "Updating..." : "Adding..."}
                                </>
                              ) : (
                                `${isEditMode ? "Update" : "Add"} SEO Page`
                              )}
                            </button>

                            <Link
                              to="/cms/manageseopage"
                              className="btn btn-secondary"
                            >
                              Cancel
                            </Link>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeoPageForm;
